/**
 * Le programme de révision d'un élève, en lecture depuis sa fiche (« Programme de révision » de
 * l'Aperçu) : `getStudyProgramsAsTutor { student_ids }`, ce que lisait
 * `StudyProgramChapterSelectionModal.js` (`readOnly`) par `StudyProgramsFilterList.js`.
 *
 * Les journées et leurs chapitres sont ceux de la boîte du lot 3 (`toUITutorStudyPrograms`, le même
 * libellé de programme, les mêmes « Jour 1 · … »). Ce que la fiche y ajoute : la progression de
 * l'élève sur chaque chapitre, que l'ancienne fiche montrait (`showChapterProgress` vrai pour un
 * tuteur et un seul élève). La lecture la sert : le backend charge
 * `studyProgramItems.chapter.studentChapters` pour cet élève (`studentsStudyPrograms`), et
 * `StudyItemComponent.js` aplatit `student_chapters[0]` sur le chapitre pour `ChapterItemComponent`.
 */
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import { toUITutorStudyPrograms, type ApiTutorStudyProgram } from "../Lives/tutorPrepareLiveAdapter"
import { chapterMasteryBar } from "../../../ui/formatters/liveSessionReportFormatter"
import { type UITutorStudyProgram } from "../../../ui/types/tutorEleves"

/** Ce que `StudentChapterResource` sert de la progression d'un élève sur un chapitre. */
type ApiStudentChapterXp = { xp_total?: number | null; xp_gained?: number | null }

/** Un programme tel que la fiche le lit : celui du lot 3, et la progression de l'élève sur chaque chapitre. */
export type ApiTutorStudentProgram = ApiTutorStudyProgram & {
    study_program_items?: Array<{
        id?: string | number
        day?: number | null
        chapter?:
            | (NonNullable<NonNullable<ApiTutorStudyProgram["study_program_items"]>[number]["chapter"]> & {
                  student_chapters?: (ApiStudentChapterXp | null)[] | null
              })
            | null
    }> | null
}

/**
 * La progression d'un chapitre, de 0 à 100 — la barre de `ChapterItemComponent.js`, lue par la barre de
 * maîtrise commune (`chapterMasteryBar` : les XP gagnés sur `max(xp_to_validate_chapter ?? 350, xp_total)`),
 * bornée et arrondie. Pas de barre (`null`) sans `xp_total`, comme l'ancienne (`!!xpTotalWidth`).
 */
export function chapterProgress(
    studentChapter: ApiStudentChapterXp | null | undefined,
    xpToValidate: number | null | undefined
): number | null {
    const total = studentChapter?.xp_total ?? 0
    if (!total) return null
    const { value } = chapterMasteryBar(studentChapter?.xp_gained ?? 0, total, xpToValidate ?? undefined)
    return Math.round(Math.max(0, Math.min(100, value)))
}

export function toUITutorStudentPrograms(
    raws: ReadonlyArray<ApiTutorStudentProgram | null | undefined> | null | undefined,
    schoolSubjects: ReadonlyArray<ApiSchoolSubject | null | undefined> | null | undefined,
    xpToValidate: number | null | undefined
): UITutorStudyProgram[] {
    const programs = toUITutorStudyPrograms(raws, schoolSubjects)
    return programs.map((program) => {
        const raw = (raws ?? []).find(
            (candidate) => candidate?.id != null && String(candidate.id) === String(program.id)
        )
        // La lecture ne porte que les chapitres de CET élève (`where user_id`) : le premier est le sien.
        const progressById = new Map<string, number | null>()
        for (const item of raw?.study_program_items ?? []) {
            const chapter = item?.chapter
            if (chapter?.id == null) continue
            progressById.set(String(chapter.id), chapterProgress(chapter.student_chapters?.[0], xpToValidate))
        }
        return {
            ...program,
            days: program.days.map((day) => ({
                ...day,
                chapters: day.chapters.map((chapter) => {
                    const progress = progressById.get(String(chapter.id)) ?? null
                    return progress != null ? { ...chapter, progress } : chapter
                }),
            })),
        }
    })
}
